import { HttpClient } from 'aurelia-fetch-client';
import { autoinject } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import * as $ from "jquery";
import * as moment from 'moment';
import numeral from 'numeral';
import 'whatwg-fetch';
import config from '../config/config';
import { FormattingHelper } from '../helpers/formatting-helper';
import { Business } from '../models/business';
import { Customer, CustomerPaymentMethod } from '../models/customer';
import { Portal } from '../models/portal';
import { Portfolio } from '../models/portfolio';
import { State } from './global-state';
import { PaymentHistory } from '../models/payment-history';
import { I18N } from 'aurelia-i18n';
import { ApiResponse } from "../models/api-response";
import { TemplateData } from '../models/template-data';
import { Language } from '../models/language';
import { FeeCalculationValue } from '../models/feeCalculationValue';
import { PaymentPlanType } from '../models/paymentPlanType';
import { UpdateCardPaymentModel } from '../models/update-card-payment-model';
import { UpdateAchPaymentModel } from '../models/update-ach-payment-model';
import { CombinedInstallmentAccountItem } from '../models/combined-installment-account-item';
import { SubmitDigitalWalletPaymentRequest } from '../models/submit-digital-wallet-payment-request';
import { DigitalWalletPaymentDetails } from '../models/digital-wallet';

@autoinject
export class PayrazrService {
    base: string;
    queryParameters: any;

    constructor(
        private globalState: State,
        private http: HttpClient,
        private router: Router,
        private i18n: I18N
    ) {
        this.base = config.serviceUrl;
		this.queryParameters = {};
		location.search.substr(1).split("&").forEach(item => { 
            const index = decodeURI(item.split("=")[0].toLowerCase());
            const value = decodeURI(item.split("=")[1]).replace("undefined", ""); 
            this.queryParameters[index] = value; 
        });
	}

	getParameterValue(key: any) {
		var key = key.toLowerCase();
		var value = this.queryParameters[key];

		if (value) {
			return value;
		}
		return "";
	}

	get awaitingResponse() {
		return this.http.isRequesting;
	}

	getPortal(portalId: any, recaptchaToken: any) {
		this.globalState.business = null;

		var url = `/portal/${portalId}`;

		if (recaptchaToken)
			url += `?captchaToken=${recaptchaToken}`;

		return this.get(url)
			.then(response => {
				if (response.portal.settings === "{}") {
					return false;
				} else {
					this.globalState.business = new Business({
						portalId: portalId,
						key: response.businessKey,
						sessionId: response.sessionId,
						name: response.businessName,
						phone: response.businessPhone,
						legalName: response.businessLegalName,
						address: response.businessAddress,
						portalSettings: new Portal(response.portal, this.i18n),
						ezpayPortfolio: response.ezpayPortfolio ? new Portfolio(response.ezpayPortfolio) : null,
						runPlannedPaymentDays: response.runPlannedPaymentDays,
						verticalMarket: response.verticalMarket,
						businessTimezone: response.businessTimezone
					});
					this.globalState.anonymousToken = response.token; // initialize

					if (!this.globalState.token) {
						this.globalState.token = this.globalState.anonymousToken;
					}

					this.globalState.business.applyBusinessSettings(response.businessSettings);

					return true;
				}
			});
	}

	getPortalId(shortPortalId: any) {
		return this.get(`/${shortPortalId}`)
			.then(response => {
				return response;
			});
	}

	login(username: any, password: any, cookie: string = null) {
		let url = `/portal/${this.globalState.business.portalId}/login`;
		let content = {
			username: username,
			password: password,
			cookieValue: cookie
		};
		return this.post(url, content);
	}

	resetSession() {
		let url = `/portal/${this.globalState.business.portalId}/reset-session`;
		return this.post(url);
	}

	logout(sessionId: any) {
		let url = `/portal/${this.globalState.business.portalId}/logout/${this.globalState.business.sessionId}`;
		return this.post(url);
	}

	startEzpay() {
		let url = `/portal/${this.globalState.business.portalId}/startEzpay`;
		return this.post(url);
	}

	refreshGlobalCustomer() {
		let customerId = this.globalState.customer.id;
		let url = `/portal/${this.globalState.business.portalId}/customer/${customerId}`;
		return this.get(url)
			.then(result => {
				this.globalState.customer = new Customer(this.globalState.business, result);

				this.globalState.associatedCustomers = [];
				for (let i of result.associatedCustomers || []) {
					this.globalState.associatedCustomers.push(new Customer(this.globalState.business, i));
				}

				return this.globalState;
			});
	}

	applyTemplate(templateName: string, templateData: TemplateData) {
		let url = `/portal/${this.globalState.business.portalId}/applyTemplate/${encodeURIComponent(templateName)}`;
		return this.post(url, templateData);
	}

	applyTemplateAndReturnHash(templateName: string, templateData: TemplateData, feeValues: Array<FeeCalculationValue> = null) {
		let url = `/portal/${this.globalState.business.portalId}/secure/applyTemplate/${encodeURIComponent(templateName)}`;
		return this.post(url, templateData);
	}

	getConfirmHtml(paymentMethod: string, templateData: TemplateData) {
		let url = `/portal/${this.globalState.business.portalId}/confirmation/${paymentMethod}`;
		return this.post(url, templateData);
	}

	getReceiptHtml(cardOrCheck: string, templateData: TemplateData) {
		let url = `/portal/${this.globalState.business.portalId}/receipt/${cardOrCheck}`;
		return this.post(url, templateData);
	}

	getDisclosure() {
		let url = `/portal/${this.globalState.business.portalId}/disclosure`;
		return this.get(url);
	}

	lookupCustomerByFilters(accountReferenceId: any, registrationId: any, registrationId2: any, registrationId3: any, zipCode: any, bucketKey?: number) {
		let content = {
			customerAccountReferenceId: accountReferenceId,
			RegistrationId: registrationId,
			RegistrationId2: registrationId2,
			RegistrationId3: registrationId3,
			ZipCode: zipCode,
			BucketKey: bucketKey
		};

		let url = `/portal/${this.globalState.business.portalId}/customer/lookup`;

		return this.post(url, content);
	}

	createOrder(accountReferenceId: any, firstName: any, lastName: any, ssn: any, email: any, addr: any, city: any, state: any, zipCode: any, preAddr: any, preCity: any, preState: any, preZip: any, year: any, type: any, bucketKey: number | null = null) {
		let url = `/portal/${this.globalState.business.portalId}/veritax/${encodeURIComponent(accountReferenceId)}/createOrder`;
		if (bucketKey) {
			url += `?bucketKey=${bucketKey}`;
		}

		let content = {
			applicant: {
				firstName: firstName,
				lastName: lastName,
				socialSecurityNumber: ssn,
				emailAddress: email,
				address: {
					addressLine: addr,
					city: city,
					state: state,
					zipCode: zipCode,
				},
				//todo: verify the veritax send right previous state when we disable, what should we send when we disable previous address
				previousAddress: {
					addressLine: preAddr,
					city: preCity,
					state: preState,
					zipCode: preZip,
				}
			},
			reportType: type,
			reportYear: year
		}
		return this.post(url, content);
	}

	getLastVeritaxOrder(accountReferenceId: any) {
		let url = `/portal/${this.globalState.business.portalId}/veritax/${encodeURIComponent(accountReferenceId)}/getLastOrder`;
		return this.get(url);
	}

	registerConsumerUser(params: any) {
		let content = {
			customerId: params.customerId,
			username: params.username,
			password: params.password,
			question1: params.question1,
			answer1: params.question1Answer,
			question2: params.question2,
			answer2: params.question2Answer,
			emailAddress: params.emailAddress,
			wantsEmailNotifications: params.wantsEmailNotifications,
			mobileNumber: params.mobilePhone,
			wantsSMSNotifications: params.enabledSMSNotifications,
			isPaperStatementsOptedIn: params.isPaperStatementsOptedIn,
			isEmailStatementsOptedIn: params.isEmailStatementsOptedIn
		}

		let url = `/portal/${this.globalState.business.portalId}/register`;
		return this.post(url, content);
	}

	//TODO: Add the correct endpoints
	savePaymentMethod(customer: any, methodName: any, paymentMethod: any) {
		console.log('savePaymentMethod', arguments);
		let url;
		let content;

		if (paymentMethod.transitNumber) {
			url = 'URL FOR ACH TOKEN';
			content = {
				account: {
					accountName: customer.name,
					aba: paymentMethod.transitNumber,
					accountNumber: paymentMethod.accountNumber,
					accountType: paymentMethod.mapAccountTypeToApi()
				},
				customerId: customer.id
			};
		} else {
			//apiService = 'URL FOR CARD TOKEN'; // portal refactor note: commented this out as it is not even used in rest of file
			content = {
				card: {
					cardNumber: paymentMethod.cardNumber,
					expirationDate: paymentMethod.expirationDate,
					cardholderName: customer.name,
					cvv: paymentMethod.cvv,
					addressLine1: customer.primaryAddress.addressLine1,
					zipCode: customer.primaryAddress.zipCode
				},
				customerId: customer.id
			};
		}

		return this.makeFakeServiceCall((accept, reject) => {
			let method;
			if (paymentMethod.transitNumber) {
				method = new CustomerPaymentMethod({
					'name': methodName,
					'type': 'ACH',
					'description': `Bank account ending in ${FormattingHelper.mask(paymentMethod.accountNumber)}`
				});
			} else {
				method = new CustomerPaymentMethod({
					'name': methodName,
					'type': 'Card',
					'description': `Card ending in ${FormattingHelper.mask(paymentMethod.cardNumber)}`
				});
			}
			customer.paymentMethods.push(method);

			accept(customer);
		});
	}

	updateUsername(newUsername: any) {
		let content = {
			NewUsername: newUsername
		};

		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(this.globalState.customer.id)}/security/username`;
		return this.put(url, content);
	}

	updatePassword(oldPassword: any, newPassword: any) {
		let content = {
			OldPassword: oldPassword,
			NewPassword: newPassword
		};

		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(this.globalState.customer.id)}/security/password`;
		return this.put(url, content);
	}

	updateSecurityQuestion(questionNumber: any, question: any, answer: any) {
		let content = {
			Question: question,
			Answer: answer
		};

		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(this.globalState.customer.id)}/security/question/${questionNumber}`;
		return this.put(url, content);
	}

	updateContactInformation(params: any) {
		let content = {
			homePhone: params.homePhone,
			workPhone: params.workPhone,
			mobilePhone: params.mobilePhone,
			smsNotificationsEnabled: params.smsNotificationsEnabled,
			emailAddress: params.emailAddress,
			emailNotificationsEnabled: params.emailNotificationsEnabled,
			isPaperStatementsOptedIn: params.isPaperStatementsOptedIn,
			isEmailStatementsOptedIn: params.isEmailStatementsOptedIn
		};

		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(params.customerId)}/contactInformation`;
		return this.put(url, content);
	}

	resendSmsVerificationCode(phoneNumber: any, customerId: any = null) {
		// Give preference to the customerId parameter.
		// If not set, default to the global cutomer in context.
		customerId = customerId || this.globalState.customer.id;

		customerId = encodeURIComponent(customerId);

		let url = `/portal/${this.globalState.business.portalId}/customer/${customerId}/resendSmsVerificationCode`;

		let content = {
			phoneNumber
		};

		return this.post(url, content);
	}

	smsPhoneNumberVerification(verificationCode: any, customerId: string = null) {
		if (!customerId) {
			customerId = this.globalState.customer.id;
		}

		let content = {
			VerificationCode: verificationCode
		};

		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(customerId)}/validateSmsVerificationCode`;
		return this.post(url, content);
	}

	updateEmailInfo(email, wantsEmailNotifications: any) {
		let content = {
			Email: email,
			WantsEmailNotifications: wantsEmailNotifications
		};

		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(this.globalState.customer.id)}/security/emailInfo`;
		return this.put(url, content);
	}

	getCustomerSecurityQuestion(content: any) {
		let url = `/portal/${this.globalState.business.portalId}/securityQuestion`;

		return new Promise((accept, reject) => {
			this.post(url, content)
				.then(response => {
					if (response.isSuccessful)
						accept(response.entity);
					else {
						switch (response.message) {
							case 'AccountLocked':
								reject(this.i18n.i18next.t('userAccountLockedContactToUnlock', { defaultValue: `This user account has been locked. Please contact us at ${this.globalState.business.portalSettings.csPhone} to unlock this account or try again later.`, phoneNumber: this.globalState.business.portalSettings.csPhone }));
								break;
							case 'NotFound':
								reject(this.i18n.i18next.t('unableToLocateAccount','Unable to locate that account'));
								break;
							case 'CustomerAccountIsInactive':
								reject(this.i18n.i18next.t('yourAccountNoLongerActive','Your account is no longer active. If you feel you have received this message in error, please contact customer service.'));
								break;
							default:
								reject(this.i18n.i18next.t('unknownError',"Unknown error"));
								break;
						}
					}
				})
				.catch(error => {
					reject(this.i18n.i18next.t('internalError',"Internal Error"));
				})
		});
	}

	checkAnswerToSecurityQuestion(customerId: any, question: any, answer: any) {
		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(customerId)}/checkSecurityQuestion`;
		let content = {
			Question: question,
			Answer: answer
		};

		return this.put(url, content);
	}

	resetForgottenPassword(customerId: any, username: any, securityQuestion: any, securityAnswer: any, newPassword: any) {
		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(customerId)}/resetPassword`;
		let content = {
			username: username,
			securityQuestion: securityQuestion,
			securityAnswer: securityAnswer,
			newPassword: newPassword
		};

		return new Promise((accept, reject) => {
			this.post(url, content)
				.then(response => {
					if (response.resultCode == 1) {
						accept(response);
					} else if (response.resultCode == 3) {
						reject(this.i18n.i18next.t('answerDoesNotMatchSecurityQuestion','The answer doesn\'t match the security question, please re-enter'));
					} else if (response.resultCode == 4) {
						reject(this.i18n.i18next.t('cannotReusePreviousPassword','You cannot re-use a previous password, please try a new one'));
					} else if (response.resultCode == 9999) {
						reject(this.i18n.i18next.t('newPasswordDoesNotMatchRequirement','The new password doesn\'t match the requirement, please try a new one'));
					} else {
						reject(this.i18n.i18next.t('unknownError', "Unknown error"));
					}
				})
				.catch(error => {
					reject(this.i18n.i18next.t('internalError', "Internal Error"));
				});
		});
	}

	calculateFee(isCardTransaction: any, portfolioName: any, zipCode: any, amountUsedToCalculateSurchargeOnIfApplicable: any, cardNumber: any,
		cardInfoKey: any = null, customerAccountKeys: any = null, paymentPlanType: PaymentPlanType = PaymentPlanType.NotSet, merchantKey: any = null) {
		cardInfoKey = cardInfoKey || null;
		cardNumber = cardNumber || null;
		amountUsedToCalculateSurchargeOnIfApplicable = amountUsedToCalculateSurchargeOnIfApplicable || 0;
		customerAccountKeys = customerAccountKeys || null;

		let url = `/portal/${this.globalState.business.portalId}/fee`;

		let requestBody = {
			paymentMethodType: (isCardTransaction ? 'card' : 'ach'),
			portfolioName,
			zipCode,
			amountUsedToCalculateSurchargeOnIfApplicable,
			cardNumber,
			cardInfoKey,
			customerAccountKeys,
			paymentPlanType,
			merchantKey
		};

		return this.post(url, requestBody);
	}

	paymentTermsRejected(account: any, bucketKey: number | null = null) {
		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(account.id)}/paymenttermsrejected`;
		if (bucketKey) {
			url += `?bucketKey=${bucketKey}`;
		}
		return this.post(url, {});
	}

	checkCardType(cardNumber: any, portfolioName: any, cardInfoKey?: any, merchantKey?: any) {
		let url = `/portal/${this.globalState.business.portalId}/checktype/card`;

		if (cardInfoKey === undefined)
			cardInfoKey = 0;

		var data = {
			portfolioName: portfolioName,
			cardNumber: cardNumber,
			cardInfoKey: cardInfoKey,
			merchantKey: merchantKey
		};
		return this.post(url, data);

	}

	submitCheckPaymentV2(customer: any, account: any, nameOnCheck: any, addressLine1: any, billingCity: any, billingState: any, zipCode: any, billingPhone: any, billingEmail: any,
		isEmailOptedIn: boolean, transactionDescription: any, isThirdPartyPayment: any, amount: any, fee: any, checkDetails: any, processorOverride: any, saveToWallet: any, discountAmount: any,
		bucketKey: number | null = null, merchantKey: number | null = null, captchaToken: string = null, isUnvalidatedEzpay: boolean = false) {
		var paymentDate = moment();
		let checkInfoKey = checkDetails.selectedPaymentMethod ? checkDetails.selectedPaymentMethod.checkInfoKey : 0;
		let customFields = checkDetails.customFields || [];
		let content = {
			checkInfoKey: checkInfoKey,
			nameOnAccount: nameOnCheck,
			aba: checkDetails.transitNumber,
			accountNumber: checkDetails.accountNumber,
			accountType: checkDetails.mapAccountTypeToApi(),
			addressLine1: addressLine1,
			billingCity: billingCity,
			billingState: billingState ? billingState : null,
			phoneNumber: billingPhone,
			email: billingEmail,
			isEmailOptedIn: isEmailOptedIn,
			zipCode: zipCode,
			paymentAmount: amount,
			ServiceFee: fee,
			checkNumber: checkDetails.checkNumber,
			description: transactionDescription,
			isThirdParty: isThirdPartyPayment,
			processorOverride: processorOverride,
			saveToWallet: saveToWallet,
			customFields: customFields.map(item => {
				return {
					Value: item.visible ? (item.value || '') : ''
				};
			}),
			discountAmount: discountAmount,
			isUnvalidatedEzpay: isUnvalidatedEzpay
		};

		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(account.id)}/sale/Ach`;
		if (bucketKey) {
			url += `?bucketKey=${bucketKey}`;
		}
		if (merchantKey) {
			url += `${bucketKey ? '&' : '?'}merchantKey=${merchantKey}`;
		}
		if (captchaToken) {
			url += `${(bucketKey || merchantKey) ? '&' : '?'}captchaToken=${captchaToken}`;
		}

		return this.post(url, content)
			.then(response => {
				let result = {
                    response: response
				};
				return result;
			});
	}

	submitCardPaymentV2ForInstallmentsPayOffEarly(customer: any, account: any, nameOnCard: any, addressLine1: any, billingCity: any, billingState: any,
		zipCode: any, billingPhone: any, billingEmail: any, transactionDescription: any, isThirdPartyPayment: any, amount: any, fee: any, cardDetails: any,
		saveToWallet: any, discountAmount: number, bucketKey: number | null = null) {
		var paymentDate = moment();

		let cardInfoKey = cardDetails.selectedPaymentMethod ? cardDetails.selectedPaymentMethod.cardInfoKey : 0;
		let customFields = cardDetails.customFields || [];

		var mostRecentPaymentPlan = account.paymentPlans
			.filter(item => item.totalPayments >= 1)
			.sort((a, b) => a.paymentDate - b.paymentDate)
			.pop();

		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(account.id)}/paymentPlan/${mostRecentPaymentPlan.id}/sale/card`;
		if (bucketKey) {
			url += `?bucketKey=${bucketKey}`;
		}

		let content = {
			saveToWallet: saveToWallet,
			cardInfoKey: cardInfoKey,
			nameOnCard: nameOnCard,
			addressLine1: addressLine1,
			billingCity: billingCity,
			billingState: billingState ? billingState : null,
			phoneNumber: billingPhone,
			email: billingEmail,
			zipCode: zipCode,
			isThirdParty: isThirdPartyPayment,
			cardNumber: cardDetails.cardNumber,
			cvv: cardDetails.cvv,
			expirationDate: cardDetails.expirationDate,
			paymentAmount: amount,
			serviceFee: fee,
			description: transactionDescription,
			customFields: customFields.map(item => {
				return {
					Value: item.visible ? (item.value || '') : ''
				};
			}),
			discountAmount: discountAmount
		};

		return this.post(url, content)
			.then(response => {
				let result = {
                    response: response
				};
				return result;
			});
	}

	submitCardPaymentV2(customer: any, account: any, nameOnCard: any, addressLine1: any, billingCity: any, billingState: any, zipCode: any,
		billingPhone: any, billingEmail: any, isEmailOptedIn: boolean, transactionDescription: any, isThirdPartyPayment: any, amount: any, fee: any, cardDetails: any,
		processorOverride: any, saveToWallet: any, discountAmount: any, bucketKey: number | null = null, merchantKey: number | null = null,
		captchaToken: string = null, isUnvalidatedEzpay: boolean = false, verificationCode: string = null, mfaEmail: string = null, mfaPhoneNumber: string = null) {
		var paymentDate = moment();
		let cardInfoKey = cardDetails.selectedPaymentMethod ? cardDetails.selectedPaymentMethod.cardInfoKey : 0;
		let customFields = cardDetails.customFields || [];
		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(account.id)}/sale/card`;
		if (bucketKey) {
			url += `?bucketKey=${bucketKey}`;
		}
		if (merchantKey) {
			url += `${bucketKey ? '&' : '?'}merchantKey=${merchantKey}`;
		}
		if (captchaToken) {
			url += `${(bucketKey || merchantKey) ? '&' : '?'}captchaToken=${captchaToken}`;
		}

		let content = {
			cardInfoKey: cardInfoKey,
			nameOnCard: nameOnCard,
			addressLine1: addressLine1,
			billingCity: billingCity,
			billingState: billingState ? billingState : null,
			phoneNumber: billingPhone,
			email: billingEmail,
			isEmailOptedIn: isEmailOptedIn,
			zipCode: zipCode,
			isThirdParty: isThirdPartyPayment,
			cardNumber: cardDetails.cardNumber,
			cvv: cardDetails.cvv,
			expirationDate: cardDetails.expirationDate,
			paymentAmount: amount,
			serviceFee: fee,
			description: transactionDescription,
			processorOverride: processorOverride,
			saveToWallet: saveToWallet,
			customFields: customFields.map(item => {
				return {
					Value: item.visible ? (item.value || '') : ''
				};
			}),
			discountAmount: discountAmount,
			isUnvalidatedEzpay: isUnvalidatedEzpay,
			verificationCode,
			mfaEmail,
			mfaPhoneNumber
		};

		return this.post(url, content)
			.then(response => {
				let result = {
                    response: response
				};
				return result;
			});
	}

	submitCardDigitalWalletPayment(request: SubmitDigitalWalletPaymentRequest): Promise<any> {
		let url = `/portal/${request.portalId}/account/${encodeURIComponent(request.customerAccountId)}/sale/card/digitalWallet`;
		if (request.bucketKey) {
			url += `?bucketKey=${request.bucketKey}`;
		}
		if (request.merchantKey) {
			url += `${request.bucketKey ? '&' : '?'}merchantKey=${request.merchantKey}`;
		}

		return this.post(url, request.digitalWalletPaymentDetails)
			.then(response => {
				let result = {
					response
				};
				return result;
			});
	}

	submitDigitalWalletPaymentPlan(
		account: any,
		digitalWalletPaymentDetails: DigitalWalletPaymentDetails,
		merchantKey: number | null = null,
		bucketKey: number | null = null) {
		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(account.id)}/paymentplan/digitalWallet`;
		if (bucketKey) {
			url += `?bucketKey=${bucketKey}`;
		}
		if (merchantKey) {
			url += `${bucketKey ? '&' : '?'}merchantKey=${merchantKey}`;
		}

		let content = digitalWalletPaymentDetails;

		return this.post(url, content)
			.then(response => {
				let result = {
					response: response,
				};
				return result;
			});
	}

	submitCheckPaymentPlan(account: any, initialPayment: any, monthlyPayment: any, numberOfMonths: any, firstPaymentDate: any, name: any, address1: any,
		billingCity: any, billingState: any, billingPhone: any, billingEmail: any, isEmailOptedIn: boolean, zip: any, fee: any, transactionDescription: any, isThirdPartyPayment: any,
		checkDetails: any, maxRetries: any, isRehab: any, saveToWallet: any, discountAmount: any, bucketKey: number | null = null, merchantKey: number | null = null) {
		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(account.id)}/paymentplan/ach`;
		if (bucketKey) {
			url += `?bucketKey=${bucketKey}`;
		}
		if (merchantKey) {
			url += `${bucketKey ? '&' : '?'}merchantKey=${merchantKey}`;
		}

		let checkInfoKey = checkDetails.selectedPaymentMethod ? checkDetails.selectedPaymentMethod.checkInfoKey : 0;
		let content = {
			saveToWallet: saveToWallet,
			checkInfoKey: checkInfoKey,
			nameOnAccount: name,
			addressLine1: address1,
			billingCity: billingCity,
			billingState: billingState ? billingState : null,
			phoneNumber: billingPhone,
			email: billingEmail,
			isEmailOptedIn: isEmailOptedIn,
			zipCode: zip,
			aba: checkDetails.transitNumber,
			checkNumber: checkDetails.checkNumber,
			accountNumber: checkDetails.accountNumber,
			accountType: checkDetails.mapAccountTypeToApi(),
			numberOfPayments: numberOfMonths,
			paymentAmount: monthlyPayment,
			serviceFee: fee,
			firstPaymentDate: moment(firstPaymentDate).format('MM/DD/YYYY'),
			maxRetries: maxRetries,
			isRehab: isRehab,
			description: transactionDescription,
			isThirdParty: isThirdPartyPayment,
			discountAmount: discountAmount
		};

		return this.post(url, content)
			.then(response => {
				let result = {
                    response: response,
				};
				return result;
			});
	}

	getPlannedPaymentsByCustomerAccountId(id: any, bucketKey: number | null = null) {
		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(id)}/plannedPayments`;
		if (bucketKey) {
			url += `?bucketKey=${bucketKey}`;
		}

		return this.get(url);
	}

	getLatestActivePaymentPlanPlannedPaymentsByCustomerAccountId(id: any) {
		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(id)}/latestActivePaymentPlanPlannedPayments`;
		return this.get(url);
	}

	getPaymentPlanByCustomerAccountId(id: any) {
		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(id)}/paymentPlan`;
		return this.get(url);
	}

	getCustomerPaymentMethodsByCustomerId(customerId: string = null) {
		if (customerId === null) {
			customerId = this.globalState.customer.id;
		}
		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(customerId)}/paymentMethods`;
		return this.get(url);
	}

	addAchPaymentMethod(token: any, makePrimary: any, customerId: string = null) {
		if (!customerId) {
			customerId = this.globalState.customer.id;
		}

		let url = `/portal/${this.globalState.business.portalId}/customers/${encodeURIComponent(customerId)}/paymentMethods/ach?makePrimary=${encodeURIComponent(makePrimary || false)}`;
		return this.post(url, token);
	}

	updateAchPaymentMethod(updateAchPaymentModel: UpdateAchPaymentModel) {
		let checkInfoKey = updateAchPaymentModel.achToken.checkInfoKey;
		let url = `/portal/${this.globalState.business.portalId}/paymentMethod/ach/${encodeURIComponent(checkInfoKey)}`;
		return this.put(url, updateAchPaymentModel);
	}

	addCardPaymentMethod(token: any, makePrimary: any, customerId: string = null) {
		if (!customerId) {
			customerId = this.globalState.customer.id;
		}

		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(customerId)}/paymentMethods/card?makePrimary=${encodeURIComponent(makePrimary || false)}`;
		return this.post(url, token);
	}

	updateCardPaymentMethod(updateCardPaymentModel: UpdateCardPaymentModel) {
		let cardInfoKey = updateCardPaymentModel.cardToken.cardInfoKey;
		let url = `/portal/${this.globalState.business.portalId}/paymentMethod/card/${encodeURIComponent(cardInfoKey)}`;
		return this.put(url, updateCardPaymentModel);
	}

	removePaymentMethod(paymentMethodType: any, paymentMethodInfoKey: any, customerId: string = null) {
		if (!customerId) {
			customerId = this.globalState.customer.id;
		}

		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(customerId)}/paymentMethods/${paymentMethodType}/delete/${paymentMethodInfoKey}`;
		return this.post(url);
	}

	//paymentMethodKey = CardInfoKey or CheckInfoKey
	//paymentMethodType = Check or Credit
	setCustomerPrimaryPaymentMethod(paymentMethodKey: any, paymentMethodType: any, customerId: string = null) {
		if (!customerId) {
			customerId = this.globalState.customer.id;
		}

		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(customerId)}/paymentMethod/${encodeURIComponent(paymentMethodKey)}/primaryPaymentMethod?paymentMethodType=${encodeURIComponent(paymentMethodType)}`;
		return this.post(url);
	}

	createCheckInstallmentPaymentPlan(account: any, totalAmount: any, numberOfPayments: any, selectedPaymentDate: any, name: any, address1: any,
		billingCity: any, billingState: any, billingPhone: any, billingEmail: any, zip: any, totalServiceFee: any, singleServiceFee: any, transactionDescription: any,
		isThirdPartyPayment: any, checkDetails: any, maxRetries: any, payToday: any, frequency: any, signedFirstName: any, signedLastName: any, signedDate: any, appliedDiscount: any,
		generatedRegEHtml: any, generatedRegHashValue: any, saveToWallet: any, bucketKey: number | null = null, negotiatorActivitySessionId: string, combinedInstallmentAccounts: Array<CombinedInstallmentAccountItem>) {
		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(account.id)}/installments`;
		if (bucketKey) {
			url += `?bucketKey=${bucketKey}`;
		}

		let checkInfoKey = checkDetails.selectedPaymentMethod ? checkDetails.selectedPaymentMethod.checkInfoKey : 0;
		let content = {
			isCardPayment: false,
			achPaymentDetails: {
				saveToWallet: saveToWallet,
				checkInfoKey: checkInfoKey,
				nameOnAccount: name,
				addressLine1: address1,
				billingCity: billingCity,
				billingState: billingState ? billingState : null,
				phoneNumber: billingPhone,
				email: billingEmail,
				zipCode: zip,
				aba: checkDetails.transitNumber,
				checkNumber: checkDetails.checkNumber,
				accountNumber: checkDetails.accountNumber,
				accountType: checkDetails.mapAccountTypeToApi(),
				isThirdParty: isThirdPartyPayment
			},
			installmentSettingModel: {
				numberOfPayments: numberOfPayments,
				totalPaymentAmount: totalAmount,
				selectedPaymentDate: moment(selectedPaymentDate).format('MM/DD/YYYY'),
				payToday: payToday,
				frequency: frequency,
				zipCode: zip,
				maxRetries: maxRetries,
				appliedDiscount: appliedDiscount,
			},
			installmentAuthorizationFormInformation: {
				signedFirstName: signedFirstName,
				signedLastName: signedLastName,
				signedDate: signedDate,
				generatedRegE: generatedRegEHtml,
				hashedValue: generatedRegHashValue
			},
			totalServiceFee: totalServiceFee,
			singleServiceFee: singleServiceFee,
			description: transactionDescription,
			negotiatorActivitySessionId,
			combinedInstallmentAccountItems: combinedInstallmentAccounts
		};

		return this.post(url, content)
			.then(response => {
				let result = {
                    response: response,
				};
				return result;
			});
	}

	submitCardPaymentPlan(account: any, initialPayment: any, monthlyPayment: any, numberOfMonths: any, firstPaymentDate: any, name: any, address1: any,
		billingCity: any, billingState: any, billingPhone: any, billingEmail: any, isEmailOptedIn: boolean, zip: any, fee: any, transactionDescription: any, isThirdPartyPayment: any,
		cardDetails: any, maxRetries: any, isRehab: any, saveToWallet: any, discountAmount: any, bucketKey: number | null = null, merchantKey: number | null = null,
		digitalWalletPaymentDetails: DigitalWalletPaymentDetails | null = null, isDigitalWallet: boolean = false) {
		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(account.id)}/paymentplan/card`;
		if (bucketKey) {
			url += `?bucketKey=${bucketKey}`;
		}
		if (merchantKey) {
			url += `${bucketKey ? '&' : '?'}merchantKey=${merchantKey}`;
		}

		let content: any;

		if (isDigitalWallet) {
			content = digitalWalletPaymentDetails;
		}
		else {
			let cardInfoKey = cardDetails.selectedPaymentMethod ? cardDetails.selectedPaymentMethod.cardInfoKey : 0;
			content = {
				saveToWallet: saveToWallet,
				cardInfoKey: cardInfoKey,
				nameOnCard: name,
				addressLine1: address1,
				billingCity: billingCity,
				billingState: billingState ? billingState : null,
				phoneNumber: billingPhone,
				email: billingEmail,
				isEmailOptedIn: isEmailOptedIn,
				zipCode: zip,
				//isThirdParty: false, // portal refactor note: this is declared twice here. The one below will win.
				cardNumber: cardDetails.cardNumber,
				cvv: cardDetails.cvv,
				expirationDate: cardDetails.expirationDate,
				numberOfPayments: numberOfMonths,
				paymentAmount: monthlyPayment,
				serviceFee: fee,
				firstPaymentDate: moment(firstPaymentDate).format('MM/DD/YYYY'),
				maxRetries: maxRetries,
				isRehab: isRehab,
				description: transactionDescription,
				isThirdParty: isThirdPartyPayment,
				discountAmount: discountAmount
			};
		}

		return this.post(url, content)
			.then(response => {
				let result = {
					response: response,
				};
				return result;
			});
	}

	createCardInstallmentPaymentPlan(account: any, totalAmount: any, numberOfPayment: any, selectedPaymentDate: any, name: any, address1: any, billingCity: any,
		billingState: any, billingPhone: any, billingEmail: any, zip: any, totalServiceFee: any, singleServiceFee: any, transactionDescription: any,
		isThirdPartyPayment: any, cardDetails: any, maxRetries: any, payToday: any, frequency: any, signedFirstName: any, signedLastName: any, signedDate: any,
		appliedDiscount: any, generatedRegEHtml: any, generatedRegHashValue: any, saveToWallet: any, negotiatorActivitySessionId: string, combinedInstallmentAccounts: Array<CombinedInstallmentAccountItem>,
		isDigitalWallet: boolean, digitalWalletPaymentDetails: DigitalWalletPaymentDetails) {
		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(account.id)}/installments`;
		let cardInfoKey = cardDetails?.selectedPaymentMethod ? cardDetails.selectedPaymentMethod.cardInfoKey : 0;
		let content = {
			isCardPayment: true,
			cardPaymentDetails: {
				saveToWallet: saveToWallet,
				cardInfoKey: cardInfoKey,
				nameOnCard: name,
				addressLine1: address1,
				billingCity: billingCity,
				billingState: billingState ? billingState : null,
				phoneNumber: billingPhone,
				email: billingEmail,
				zipCode: zip,
				isThirdParty: isThirdPartyPayment,
				cardNumber: cardDetails?.cardNumber,
				cvv: cardDetails?.cvv,
				expirationDate: cardDetails?.expirationDate,
			},
			digitalWalletPaymentDetails: digitalWalletPaymentDetails,
			installmentSettingModel: {
				numberOfPayments: numberOfPayment,
				totalPaymentAmount: totalAmount,
				selectedPaymentDate: moment(selectedPaymentDate).format('MM/DD/YYYY'),
				payToday: payToday,
				frequency: frequency,
				zipCode: zip,
				maxRetries: maxRetries,
				appliedDiscount: appliedDiscount
			},
			description: transactionDescription,
			installmentAuthorizationFormInformation: {
				signedFirstName: signedFirstName,
				signedLastName: signedLastName,
				signedDate: signedDate,
				generatedRegE: generatedRegEHtml,
				hashedValue: generatedRegHashValue
			},
			totalServiceFee: totalServiceFee,
			singleServiceFee: singleServiceFee,
			negotiatorActivitySessionId,
			combinedInstallmentAccountItems: combinedInstallmentAccounts,
			isDigitalWallet: isDigitalWallet
		};

		return this.post(url, content)
			.then(response => {
				let result = {
                    response: response,
				};
				return result;
			});
	}

	createRecurringPaymentPlan(isCardPayment: any, account: any, totalAmount: any, firstPaymentDate: any, lastPaymentDate: any, firstPaymentFee: any, subsequentPaymentFee: any,
		name: any, address1: any, billingCity: any, billingState: any, billingPhone: any, billingEmail: any, zip: any, totalServiceFee: any, singleServiceFee: any, transactionDescription: any,
		isThirdPartyPayment: any, paymentDetails: any, maxRetries: any, frequency: any, signedFirstName: any, signedLastName: any, signedDate: any, generatedRegEHtml: any, generatedRegHashValue: any,
		saveToWallet: any, bucketKey: number | null = null, isDigitalWallet: boolean = false) {
		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(account.id)}/recurring`;
		if (bucketKey) {
			url += `?bucketKey=${bucketKey}`;
		}

		let _lastPaymentDate = lastPaymentDate ? moment(lastPaymentDate).format('MM/DD/YYYY') : null;

		let content: any = {
			isCardPayment: isCardPayment,
			recurringSettingModel: {
				paymentAmount: totalAmount,
				firstPaymentDate: moment(firstPaymentDate).format('MM/DD/YYYY'),
				lastPaymentDate: _lastPaymentDate,
				firstPaymentFee: firstPaymentFee,
				subsequentPaymentFee: subsequentPaymentFee,
				frequency: frequency,
				zipCode: zip,
				maxRetries: maxRetries
			},
			description: transactionDescription,
			recurringAuthorizationFormInformation: {
				signedFirstName: signedFirstName,
				signedLastName: signedLastName,
				signedDate: signedDate,
				generatedRegE: generatedRegEHtml,
				hashedValue: generatedRegHashValue
			},
			totalServiceFee: totalServiceFee,
			singleServiceFee: singleServiceFee,
			isDigitalWallet: isDigitalWallet
		};

		if (isCardPayment) {
			if (isDigitalWallet) {
				content.digitalWalletPaymentDetails = paymentDetails as DigitalWalletPaymentDetails;
			}
			else {
				content.cardPaymentDetails = {
					saveToWallet: saveToWallet,
					cardInfoKey: paymentDetails.selectedPaymentMethod ? paymentDetails.selectedPaymentMethod.cardInfoKey : 0,
					nameOnCard: name,
					addressLine1: address1,
					billingCity: billingCity,
					billingState: billingState ? billingState : null,
					phoneNumber: billingPhone,
					email: billingEmail,
					zipCode: zip,
					isThirdParty: isThirdPartyPayment,
					cardNumber: paymentDetails.cardNumber,
					cvv: paymentDetails.cvv,
					expirationDate: paymentDetails.expirationDate
				};
			}
		}
		else {
			content.achPaymentDetails = {
				saveToWallet: saveToWallet,
				checkInfoKey: paymentDetails.selectedPaymentMethod ? paymentDetails.selectedPaymentMethod.checkInfoKey : 0,
				nameOnAccount: name,
				addressLine1: address1,
				billingCity: billingCity,
				billingState: billingState ? billingState : null,
				phoneNumber: billingPhone,
				email: billingEmail,
				zipCode: zip,
				aba: paymentDetails.transitNumber,
				checkNumber: paymentDetails.checkNumber,
				accountNumber: paymentDetails.accountNumber,
				accountType: paymentDetails.mapAccountTypeToApi(),
				isThirdParty: isThirdPartyPayment
			};
		}

		return this.post(url, content)
			.then(response => {
				let result = {
                    response: response
				};
				return result;
			});
	}

	cancelPaymentPlan(planId: any) {
		let url = `/portal/${this.globalState.business.portalId}/paymentPlan/${planId}/cancelplan`;
		return this.get(url)
	}

	submitPaymentPlanOffer(accountId: any, initialPayment: any, monthlyPayment: any, numberOfMonths: any) {
		var self = this;
		return this.makeFakeServiceCall((accept, reject) => {
			var account = self.globalState.accounts.find((item) => { return item.id === accountId; });
			if (account) {
				var total = numeral().unformat(initialPayment) + numeral().unformat(monthlyPayment) * numeral().unformat(numberOfMonths);

				if (total >= account.balance * 0.85) {
					accept({ accepted: true });
				} else {
					accept({
						accepted: false,
						counterOffers: [
							{
								initialPayment: 123.75,
								monthlyPayment: 250,
								numberOfMonths: 4,
								totalPayment: 1123.75
							},
							{
								initialPayment: 223.75,
								monthlyPayment: 300,
								numberOfMonths: 3,
								totalPayment: 1123.75
							},
							{
								initialPayment: 300,
								monthlyPayment: 400,
								numberOfMonths: 2,
								totalPayment: 1100
							},
							{
								initialPayment: 1050,
								monthlyPayment: 0,
								numberOfMonths: 0,
								totalPayment: 1050
							}
						]
					});
				}
			} else {
				reject('Cannot locate customer account');
			}
		}, 2000);
	}

	getPaymentHistory(customerId: string = null): Promise<PaymentHistory[]> {
		if (!customerId) {
			customerId = this.globalState.customer.id;
		}

		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(customerId)}/history`;
		return this.get(url);
	}

	getRehabDocumentsList(customerId: string = null) {
		if (!customerId) {
			customerId = this.globalState.customer.id;
		}

		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(customerId)}/docs`;
		return this.get(url);
	}

	saveDocument(accountId: any, document: any) {
		let url = `/PayrazrApiWeb/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(accountId)}/document`;
		var data = new FormData();
		data.append("documentData", document);

		console.log(`Saving document to ${url}`);
		let documentPromise = $.ajax({
			type: "POST",
			url: url,
			contentType: false,
			processData: false,
			data: data
		});
		return Promise.all([documentPromise]);
	}

	computePlannedPayments(
		accountId: any,
		totalAmount: any,
		numberOfPayments: any,
		SelectedPaymentDate: any,
		payToday: any,
		frequency: any,
		zipCode: any,
		paymentMethodType: any,
		cardNumber: any,
		cardInfoKey: any = null,
		computePlanWithoutFees: boolean = false) {
		cardInfoKey = cardInfoKey || null;
		cardNumber = cardInfoKey || null;

		let url = `/portal/${this.globalState.business.portalId}/account/${encodeURIComponent(accountId)}/computePlanedPayments`;
		const content =
		{
			numberOfPayments,
			totalPaymentAmount: totalAmount,
			SelectedPaymentDate,
			payToday,
			frequency,
			zipCode,
			paymentMethodType,
			cardNumber,
			cardInfoKey,
			computePlanWithoutFees
		};
		return this.post(url, content)
			.then(response => {
				let result = {
					plannedPayments: response,
				};
				return result;
			});
	}

	getMessages() {
		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(this.globalState.customer.id)}/messages`;
		return this.get(url);
	}

	markMessageAsRead(messages: any) {

		let content = messages;
		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(this.globalState.customer.id)}/messages/markasread`;
		return this.post(url, content);
		// let self = this;

		// // real code will have to call services not yet written to gather messages
		// return this.makeFakeServiceCall((accept, reject) => {
		// for (let ii = self.globalState.business.messages.length - 1; ii >= 0 ; --ii) {
		// if (self.globalState.business.messages[ii].id === messageId) {
		// self.globalState.business.messages.splice(ii, 1);
		// }
		// }
		// for (let ii = self.globalState.customer.messages.length - 1; ii >= 0 ; --ii) {
		// if (self.globalState.customer.messages[ii].id === messageId) {
		// self.globalState.customer.messages.splice(ii, 1);
		// }
		// }
		// accept();
		// }, 1000);
	}

	formatResultCode(code: any, codeDetail: any) {
		if (code === 1) {
			return 'Success';
		} else {
			return `Error: ${code} (detail: ${codeDetail})`;
		}
	}

	//todo: check if we can change customerAccountNumber to customerAccountId
	sendEmail(fromAddress: any, toAddress: any, subject: any, message: any, customerAccountNumber: any, bucketKey: number | null = null) {
		let url = `/portal/${this.globalState.business.portalId}/email/send`;
		if (bucketKey) {
			url += `?bucketKey=${bucketKey}`;
		}

		let content = {
			fromAddress: fromAddress,
			toAddress: toAddress,
			subject: subject,
			message: message,
			customerAccountNumber: customerAccountNumber,
			bucketKey: bucketKey
		};
		return this.post(url, content);
	}

	sendEmailWithApplyTemplateRequest(fromAddress: any, toAddress: any, subject: any, applyTemplateRequest: any, customerAccountNumber: any, bucketKey: number | null = null) {
		let url = `/portal/${this.globalState.business.portalId}/email/sendWithApplyTemplateRequest`;
		if (bucketKey) {
			url += `?bucketKey=${bucketKey}`;
		}

		let content = {
			fromAddress: fromAddress,
			toAddress: toAddress,
			subject: subject,
			applyTemplateRequest: applyTemplateRequest,
			customerAccountNumber: customerAccountNumber,
			bucketKey: bucketKey
		};
		return this.post(url, content);
	}

	sendCustomerInvoice(customerId: any) {
		let url = `/portal/${this.globalState.business.portalId}/customer/${encodeURIComponent(customerId)}/invoiceNotification`;
		return this.post(url);
	}

	sendMfaVerificationCode(mfaDetails: any, customerId: any = null) {
		// Give preference to the customerId parameter.
		// If not set, default to the global cutomer in context.
		customerId = customerId || this.globalState.customer.id;

		customerId = encodeURIComponent(customerId);

		let url = `/portal/${this.globalState.business.portalId}/customer/${customerId}/sendMfaVerificationCode`;

		return this.post(url, mfaDetails);
	}

	verifyMfaVerificationCode(mfaDetails: any, customerId: any = null) {
		customerId = customerId || this.globalState.customer.id;
		customerId = encodeURIComponent(customerId);

		let url = `/portal/${this.globalState.business.portalId}/customer/${customerId}/verifyMfaVerificationCode`;
		return this.post(url, mfaDetails);
	}

	sendUnvalidatedEzPayVerificationCode(customerId: string, mfaDetails: any) {
		customerId = encodeURIComponent(customerId);

		let url = `/portal/${this.globalState.business.portalId}/account/${customerId}/sendUnvalidatedEzPayVerificationCode`;

		return this.post(url, mfaDetails);
	}

	verifyUnvalidatedEzPayVerificationCode(customerId: string, mfaDetails: any) {
		customerId = encodeURIComponent(customerId);

		let portalId = this.globalState.business.portalId;

		let url = `/portal/${portalId}/account/${customerId}/verifyUnvalidatedEzPayVerificationCode`;

		return this.post(url, mfaDetails);
	}

	replaceSpecialCharactersWithUnderscore(str: any) {
		if (str.length === 0)
			return str;
		var s = str.replace(/[^0-9a-zA-Z]+/g, '_');
		return s;
	}

	validateEmail(email: any) {
		var reg = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		return reg.test(email);
	}

	referenceIdWrapper(referenceId: any, separator: any, merchantThirdPartyIntegrationPlatform: any) {
		if (!referenceId) return referenceId;
		if (merchantThirdPartyIntegrationPlatform === 2) {
			return referenceId.split(separator)[0];
		}
		return referenceId;
	}

	getISO6391Languages() {
		let url = `/locales/iso6391Lanaguages`;
		return this.get(url);
	}

	get(url: any) {
		let init = {
			method: 'get',
			headers: new Headers({
				'Content-Type': 'application/json',
				'PayrazrSessionId': this.globalState.business ? this.globalState.business.sessionId : null
			})
		}

		return this.http.fetch(`${this.base}${url}`, init)
			.then(r => r.json());
	}

	put(url: any, content: any) {
		let request = JSON.stringify(content);

		let init = {
			method: 'put',
			headers: new Headers({
				'Content-Type': 'application/json',
				'PayrazrSessionId': this.globalState.business.sessionId
			}),
			body: request
		};

		return this.http.fetch(`${this.base}${url}`, init)
			.then(response => {
				if (response.status == 204) {
					return '';
				} else {
					return response.json();
				}
			})
			.then(response => {
				return response;
			});
	}

	post(url: any, content?: any) {
		let request = JSON.stringify(content);

		let init = {
			method: 'post',
			headers: new Headers({
				'Content-Type': 'application/json',
				'PayrazrSessionId': this.globalState.business.sessionId
			}),
			body: request
		};

		return this.http.fetch(`${this.base}${url}`, init)
			.then(response => {
				if (response.status == 204) {
					return '';
				} else {
					return response.json();
				}
			})
			.then(response => {
				return response;
			});
	}

	getFile(fileUrl: string): Promise<any> {
		return new Promise((resolve, reject) => {
			let xhttp = new XMLHttpRequest();
			xhttp.open('GET', `${fileUrl}`, true);
			xhttp.responseType = 'blob';
			xhttp.setRequestHeader('PayrazrSessionId', this.globalState.business.sessionId);
			xhttp.setRequestHeader('Authorization', `Bearer ${this.globalState.token}`);
			xhttp.send();
			xhttp.onload = () => {
				if (xhttp.status === 200) {
					resolve(xhttp.response);
				} else {
					reject(xhttp.response);
				}
			};
		});
	}

	// This doesn't use the baseurl by default. Pass in the full url
	downloadFile(fileUrl: any, saveAsFilename: any) {
		var xhttp = new XMLHttpRequest();
		xhttp.onreadystatechange = () => {
			if (xhttp.readyState === 4 && xhttp.status === 200) {
				const filename = (saveAsFilename ? saveAsFilename : '');
				this.createAndOpenFile(xhttp.response, filename);
			} else if(xhttp.readyState === 4 && xhttp.status === 404) {
				// TODO: Use something other than alert here, like toast
				alert('An error occured when attempting to download file.');
			}
		};
		xhttp.open("GET", `${fileUrl}`, true);
		xhttp.responseType = "blob";
		xhttp.setRequestHeader("PayrazrSessionId", this.globalState.business.sessionId);
		xhttp.setRequestHeader('Authorization', `Bearer ${this.globalState.token}`);
		xhttp.send();
	}

	createAndOpenFile(blobString: string, fileName: string) {
		let a: HTMLAnchorElement = document.createElement("a");
		document.body.appendChild(a)

		if (window.navigator.msSaveOrOpenBlob) {
			// ie11 
			const blob: Blob = new Blob([blobString]);
			a.click = () => {
				window.navigator.msSaveOrOpenBlob(blob, fileName);
			}
		} else {
			// Real browsers
			const downloadUrl = URL.createObjectURL(blobString);
			a.href = downloadUrl;
			a.download = fileName;
		}

		a.click();
	}

	makeFakeServiceCall(action: any, delay?: any) {
		return new Promise((accept, reject) => {
			setTimeout(() => {
				action(accept, reject);
			}, delay);
		});
	}
}


/*
constructor(http, httpUtility, appConfig) {
    this.appConfig = appConfig;
    this.httpUtility = httpUtility;

    http.configure(config => {
      config
        .withDefaults({
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'Fetch',
            'Authorization': `Bearer ${Cookie.get('blahblahblah')}`
          }
        })
        .withInterceptor({
          request: (request) => { return this.httpUtility.requestInterceptor(request); },
          requestError: (requestError) => { return this.httpUtility.requestErrorInterceptor(requestError); },
          response: (response) => { return this.httpUtility.responseInterceptor(response); },
          responseError: (responseError) => { return this.httpUtility.responseErrorInterceptor(responseError); }
        })
        .withBaseUrl(this.appConfig.apiUri)
    });

    this.http = http;
  }
*/
